// src/utils/downloadUtils.js
// ✅ OPRAVENÁ VERZE s podporou kategorií

import { API_BASE_URL } from '../services/api.js';

/**
 * Stáhne soubor správným způsobem
 * @param {Object} file - File object 
 * @param {string} projectId - Project ID
 */
export const downloadFile = async (file, projectId) => {
  try {
    const token = localStorage.getItem('token');
    const fileId = file.id || file._id;
    
    // ✅ KLÍČOVÁ OPRAVA: Použít absolutní URL místo relativní
    const downloadUrl = `${API_BASE_URL}/api/projects/${projectId}/file/${fileId}/download`;
    
    console.log('🔍 DOWNLOAD called:', downloadUrl);
    
    // ✅ Fetch soubor jako blob s absolutní URL
    const response = await fetch(downloadUrl, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // ✅ Konvertuj na blob a stáhni
    const blob = await response.blob();
    
    console.log('✅ Download successful:', {
      size: blob.size,
      type: blob.type,
      filename: file.originalName || file.fileName || file.file_name
    });
    
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = file.originalName || file.fileName || file.file_name || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('❌ Download failed:', error);
    alert(`Stahování selhalo: ${error.message}`);
  }
};

/**
 * ✅ OPRAVENÁ funkce - Stáhne všechny soubory jako ZIP s podporou kategorií
 * @param {Array} files - Array of files
 * @param {string} projectId - Project ID  
 * @param {Object} options - Download options
 */
export const downloadAllFiles = async (files, projectId, options = {}) => {
  if (files.length === 0) return;

  const {
    organizeByCategory = false,
    categoryLabels = [],
    zipFileName = null
  } = options;

  try {
    // Dynamický import JSZip
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const token = localStorage.getItem('token');

    console.log(`Starting bulk download of ${files.length} files...`);

    // ✅ Vytvoř mapování kategorie → label pro složky
    const getCategoryLabel = (categoryCode) => {
      if (!organizeByCategory) return '';
      
      const category = categoryLabels.find(cat => cat.code === categoryCode);
      return category ? (category.labels?.en || category.label || categoryCode) : categoryCode;
    };

    // ✅ Stáhni všechny soubory s tříděním do kategorií
    for (const file of files) {
      try {
        const fileId = file.id || file._id;
        const fileName = file.originalName || file.fileName || file.file_name || `file_${fileId}`;
        
        // ✅ Urči kategorii souboru
        const fileCategory = file.tag || file.tags || file.category || 'document';
        const categoryLabel = getCategoryLabel(fileCategory);
        
        // ✅ Vytvoř cestu k souboru (s kategorií nebo bez)
        const filePath = organizeByCategory && categoryLabel && fileCategory !== 'all' 
          ? `${categoryLabel}/${fileName}`
          : fileName;
        
        // Zkus base64 nejdřív (rychlejší)
        if (file.base64) {
          const base64Data = file.base64.replace(/^data:.*?;base64,/, '');
          zip.file(filePath, base64Data, {base64: true});
          console.log(`✅ Added ${filePath} from base64`);
        } else {
          // ✅ KLÍČOVÁ OPRAVA: Stáhni z R2 s absolutní URL
          const downloadUrl = `${API_BASE_URL}/api/projects/${projectId}/file/${fileId}/download`;
          
          console.log('🔍 BULK DOWNLOAD called:', downloadUrl);
          
          const response = await fetch(downloadUrl, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            zip.file(filePath, blob);
            console.log(`✅ Added ${filePath} from R2 (${blob.size} bytes)`);
          } else {
            console.warn(`❌ Failed to download ${fileName}: ${response.status}`);
          }
        }
      } catch (error) {
        console.warn(`❌ Failed to download ${file.originalName || file.fileName}:`, error);
      }
    }

    console.log('Generating ZIP file...');
    
    // ✅ Vygeneruj a stáhni ZIP
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
    
    const url = URL.createObjectURL(zipBlob);
    
    const link = document.createElement('a');
    link.href = url;
    
    // ✅ Lepší název ZIP souboru
    const defaultName = organizeByCategory 
      ? `project_${projectId}_files_by_category.zip`
      : `project_${projectId}_files.zip`;
    
    link.download = zipFileName || defaultName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`✅ ZIP download completed: ${zipBlob.size} bytes`);
    
    // ✅ Zobraz summary
    if (organizeByCategory) {
      const categories = [...new Set(files.map(f => f.tag || f.tags || f.category || 'document'))];
      console.log(`📁 Files organized into ${categories.length} categories:`, categories);
    }
    
  } catch (error) {
    console.error('Bulk download failed:', error);
    alert(`Stahování selhalo: ${error.message}`);
  }
};

/**
 * ✅ NOVÁ funkce - Získá statistiky kategorií pro zobrazení
 * @param {Array} files - Array of files
 * @param {Array} categoryLabels - Array of category definitions
 */
export const getFileCategoryStats = (files, categoryLabels = []) => {
  const stats = {};
  
  files.forEach(file => {
    const category = file.tag || file.tags || file.category || 'document';
    stats[category] = (stats[category] || 0) + 1;
  });
  
  // Přidej labels
  const result = Object.entries(stats).map(([code, count]) => {
    const categoryDef = categoryLabels.find(cat => cat.code === code);
    return {
      code,
      label: categoryDef ? (categoryDef.labels?.en || categoryDef.label || code) : code,
      count
    };
  });
  
  return result.sort((a, b) => b.count - a.count);
};